import { useRef, useState } from 'react'
import { Box, Typography, IconButton, Tooltip, Chip, CircularProgress } from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import { FS_XS, FS_XL, ICON_XS, ICON_SM, FS_SM } from '../common/theme'
import TaQueryBar from './TaQueryBar'
import TaPadlock from './TaPadlock'
import TaElements from './TaElements'
import type { Question } from '../types/question'
import TaWordCount from './TaWordCount'
import type { UndoRedoHandle } from '../common/useUndoRedoHistory'

interface BidQuestionStoryboardTabProps {
  question: Question
  index: number
  onStructureChange: (html: string) => void
  isLocked: boolean
  onToggleLock: () => void
  // Optional richer-mode props (used by ResponseBuilderTab).
  onRegenStructure?: () => void
  regenInProgress?: boolean
  // RBT lets the user turn structure generation off; chip toggles inline.
  useStructure?: boolean
  onUseStructureChange?: (v: boolean) => void
  // Wipe the storyboard (structure + tweaks). Caller is expected to gate
  // this behind a confirm.
  onClear?: () => void
}

export default function BidQuestionStoryboardTab({
  question, index, onStructureChange, isLocked, onToggleLock,
  onRegenStructure, regenInProgress,
  useStructure, onUseStructureChange, onClear
}: BidQuestionStoryboardTabProps) {
  // Imperative handle on the inner TaQueryBar - lets the toolbar buttons
  // drive Undo / Redo without TaQueryBar needing its own toolbar.
  const editorRef = useRef<UndoRedoHandle>(null)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const hasContent = !!question.structure || (question.structuretweaks?.length ?? 0) > 0
  // Brief answers don't use a storyboard.
  if ((question.replylimit?.answerlimit ?? 0) <= 50) {
    return (
      <Typography variant="caption" sx={{ opacity: 0.5, fontSize: FS_XS }}>
        Not used for Brief answers
      </Typography>
    )
  }

  const showUseToggle = onUseStructureChange !== undefined && useStructure !== undefined
  const useStructureActive = useStructure !== false

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM, px: 0.5 }}>Your Storyboard</Typography>
        <TaPadlock isLocked={isLocked} onToggle={onToggleLock} />
        <TaElements
          mode="structure"
          question={question}
          disabled={isLocked}
          testId={`pmq_addstructuretweaks_${index}`}
        />
        {/* Undo / Redo - drive the inner TaQueryBar's history via its
            imperative handle. Disabled state reflects the live buffer
            bounds reported by onHistoryChange. */}
        <Tooltip title="Undo">
          <span>
            <IconButton
              size="small"
              onClick={() => editorRef.current?.undo()}
              disabled={!canUndo}
              sx={{ p: 0.25 }}
            >
              <UndoIcon sx={{ fontSize: ICON_XS }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo">
          <span>
            <IconButton
              size="small"
              onClick={() => editorRef.current?.redo()}
              disabled={!canRedo}
              sx={{ p: 0.25 }}
            >
              <RedoIcon sx={{ fontSize: ICON_XS }} />
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {showUseToggle && (
          <Chip
            size="small"
            label={useStructureActive ? 'On' : 'Off'}
            color={useStructureActive ? 'primary' : 'default'}
            onClick={() => onUseStructureChange?.(!useStructureActive)}
            sx={{ height: 20, fontSize: '0.68rem', cursor: 'pointer' }}
          />
        )}
        {onRegenStructure && (
          <Tooltip title={question.structure ? 'Regenerate Storyboard' : 'Generate Storyboard'}>
            <span>
              <IconButton
                size="small"
                onClick={onRegenStructure}
                disabled={regenInProgress || isLocked || (showUseToggle && !useStructureActive)}
                sx={{ p: 0.25 }}
              >
                {regenInProgress
                  ? <CircularProgress size={14} thickness={5} />
                  : <AutorenewIcon sx={{ fontSize: ICON_SM }} />
                }
              </IconButton>
            </span>
          </Tooltip>
        )}
        <TaWordCount
          text={question.structure as any}
          question={question}
        />
        {onClear && hasContent && (
          <Tooltip title="Clear Storyboard">
            <span>
              <IconButton size="small" disabled={isLocked} onClick={onClear} sx={{ p: 0.25 }}>
                <CancelOutlinedIcon sx={{ fontSize: FS_XL }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Structure text - editable outline. RBT can disable the editor
          entirely by passing useStructure=false. */}
      {(!showUseToggle || useStructureActive) && (
        <TaQueryBar
          htmlContent={question.structure ?? ''}
          onChange={onStructureChange}
          editable={!question.locks?.structure}
          placeholder="Enter the answer structure..."
          id={`pmqstructure_${index}`}
          handleRef={editorRef}
          onHistoryChange={({ canUndo, canRedo }) => {
            setCanUndo(canUndo)
            setCanRedo(canRedo)
          }}
        />
      )}
    </Box>
  )
}
